import { Throttler } from "@tanstack/react-pacer";
import type {
    EventLog,
    EventLogStatusEnum,
    GlobalSettings,
    HistoryState,
    PartStatus,
    PerformanceMetrics,
    PerformanceSample,
    ProductReferenceCalculationEntry,
    SmartNumberChangeEntry,
    TrackingEvent,
} from "@/features/diagnostics/types";
import type { ProductData, SmartNumberMapping } from "@/features/sub-families/types";
import type { DefaultTrackingData } from "@/features/tracking/types";
import StoreSubscriber from "@/lib/store-subscriber.ts";
import type { Nullable } from "@/types";

const PERFORMANCE_THROTTLE_MS = 1_000;

class DiagnosticsStore extends StoreSubscriber {
    static #instance: DiagnosticsStore;

    #eventLogs: EventLog[];
    #partStatuses: PartStatus[];
    #globals: Nullable<GlobalSettings>;
    #trackingEvents: TrackingEvent[];
    #historyState: HistoryState[];
    #smartNumberChanges: SmartNumberChangeEntry[];
    #productReferenceCalculations: ProductReferenceCalculationEntry[];
    #performance: PerformanceMetrics;
    #products: ProductData;

    #pendingSamples: PerformanceSample[];
    readonly #performanceThrottler: Throttler<() => void>;

    private constructor() {
        super();

        this.#eventLogs = [];
        this.#partStatuses = [];
        this.#globals = null;
        this.#trackingEvents = [];
        this.#historyState = [];
        this.#smartNumberChanges = [];
        this.#productReferenceCalculations = [];
        this.#performance = {
            current: null,
            fps: 0,
            avgFrameTime: 0,
            maxFrameTime: 0,
            totalFrames: 0,
            modelLoadTime: null,
        };
        this.#pendingSamples = [];
        this.#products = {};

        this.#performanceThrottler = new Throttler(() => this.#flushPerformanceSamples(), {
            wait: PERFORMANCE_THROTTLE_MS,
        });
    }

    get eventLogs() {
        return this.#eventLogs;
    }

    get partStatuses() {
        return this.#partStatuses;
    }

    get globals() {
        return this.#globals;
    }

    get trackingEvents() {
        return this.#trackingEvents;
    }

    get historyState() {
        return this.#historyState;
    }

    get smartNumberChanges() {
        return this.#smartNumberChanges;
    }

    get productReferenceCalculations() {
        return this.#productReferenceCalculations;
    }

    get performance() {
        return this.#performance;
    }

    get products() {
        return this.#products;
    }

    collectEventLog(status: EventLogStatusEnum, message: string) {
        this.updateStore(() => {
            this.#eventLogs = [{ status, message, timestamp: Date.now() }, ...this.#eventLogs];
        });
    }

    collectPartStatus(partStatus: PartStatus) {
        this.updateStore(() => {
            const existing = this.#partStatuses.find((p) => p.partNo === partStatus.partNo);

            this.#partStatuses = existing
                ? this.#partStatuses.map((p) => (p.partNo === partStatus.partNo ? partStatus : p))
                : [...this.#partStatuses, partStatus];
        });
    }

    collectGlobals(globals: GlobalSettings) {
        this.updateStore(() => {
            this.#globals = globals;
        });
    }

    collectTrackingEvent(data: DefaultTrackingData) {
        this.updateStore(() => {
            const id = crypto.randomUUID();
            const timestamp = Date.now();

            this.#trackingEvents = [{ id, event: data.name, data, timestamp }, ...this.#trackingEvents];
        });
    }

    collectHistoryState(id: string, smartNumber: string, smartNumberMapping: SmartNumberMapping) {
        this.updateStore(() => {
            this.#historyState = [
                { id, smartNumber, smartNumberMapping, timestamp: Date.now() },
                ...this.#historyState,
            ];
        });
    }

    removeHistoryStates(ids: string[]) {
        this.updateStore(() => {
            this.#historyState = this.#historyState.filter((entry) => !ids.includes(entry.id));
        });
    }

    collectSmartNumberChange(entry: Omit<SmartNumberChangeEntry, "id" | "timestamp">) {
        this.updateStore(() => {
            this.#smartNumberChanges = [
                { ...entry, id: crypto.randomUUID(), timestamp: Date.now() },
                ...this.#smartNumberChanges,
            ];
        });
    }

    collectProductReferenceCalculation(entry: Omit<ProductReferenceCalculationEntry, "id" | "timestamp">) {
        this.updateStore(() => {
            this.#productReferenceCalculations = [
                { ...entry, id: crypto.randomUUID(), timestamp: Date.now() },
                ...this.#productReferenceCalculations,
            ];
        });
    }

    collectPerformanceSample(sample: PerformanceSample) {
        this.#pendingSamples.push(sample);
        this.#performanceThrottler.maybeExecute();
    }

    collectModelLoadTime(ms: number) {
        this.updateStore(() => {
            this.#performance = { ...this.#performance, modelLoadTime: ms };
        });
    }

    collectProductInfo(products: ProductData) {
        this.updateStore(() => {
            this.#products = products;
        });
    }

    #flushPerformanceSamples() {
        if (this.#pendingSamples.length === 0) return;

        const samples = this.#pendingSamples;
        this.#pendingSamples = [];

        const prev = this.#performance;
        const newTotal = prev.totalFrames + samples.length;

        const totalDeltaTime = samples.reduce<number>((sum, sample) => sum + sample.deltaTime, 0);
        const avgDeltaTime = totalDeltaTime / samples.length;

        const sumFrameTime = samples.reduce<number>((sum, sample) => sum + sample.frameTime, 0);
        const maxFrameTime = Math.max(prev.maxFrameTime, ...samples.map((sample) => sample.frameTime));

        const prevSum = prev.avgFrameTime * prev.totalFrames;
        const avgFrameTime = (prevSum + sumFrameTime) / newTotal;

        this.updateStore(() => {
            this.#performance = {
                current: samples.slice(-1)[0] ?? null,
                fps: Math.round(1_000 / avgDeltaTime),
                avgFrameTime,
                maxFrameTime,
                totalFrames: newTotal,
                modelLoadTime: prev.modelLoadTime,
            };
        });
    }

    static getInstance() {
        DiagnosticsStore.#instance ??= new DiagnosticsStore();
        return DiagnosticsStore.#instance;
    }
}

export default DiagnosticsStore.getInstance();
